/**
 * 系统设置页面
 */

import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  ServerIcon,
  CircleStackIcon, // DatabaseIcon 在新版本中被重命名为 CircleStackIcon
  ShieldCheckIcon,
  DocumentTextIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { systemApi } from '@/lib/api';

export default function Settings() {
  const [activeTab, setActiveTab] = useState<'general' | 'database' | 'sql'>('general');
  const [sqlInput, setSqlInput] = useState('');

  // 获取系统信息
  const { data: systemInfo, refetch } = useQuery({
    queryKey: ['systemInfo'],
    queryFn: systemApi.getSystemInfo,
    refetchInterval: 30000,
  });

  // 执行SQL
  const executeSQLMutation = useMutation({
    mutationFn: systemApi.executeSQL,
    onSuccess: (data) => {
      toast.success('SQL执行成功');
      console.log('SQL Result:', data.result);
    },
    onError: () => {
      toast.error('SQL执行失败');
    },
  });

  const handleExecuteSQL = () => {
    if (!sqlInput.trim()) {
      toast.error('请输入SQL语句');
      return;
    }
    executeSQLMutation.mutate(sqlInput.trim());
  };

  const tabs = [
    { key: 'general', label: '基本信息', icon: Cog6ToothIcon },
    { key: 'database', label: '数据库状态', icon: CircleStackIcon },
    { key: 'sql', label: 'SQL控制台', icon: DocumentTextIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">系统设置</h1>
        <p className="mt-2 text-sm text-gray-600">
          管理系统配置和维护操作
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`
                    group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                    ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-5 w-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ServerIcon className="h-8 w-8 text-blue-500" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">版本</h3>
                      <p className="text-2xl font-bold text-gray-900">
                        {systemInfo?.version || 'v1.0.0'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ShieldCheckIcon className="h-8 w-8 text-green-500" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">环境</h3>
                      <p className="text-2xl font-bold text-gray-900">
                        {systemInfo?.environment || 'development'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CircleStackIcon className="h-8 w-8 text-purple-500" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">数据库</h3>
                      <p className="text-2xl font-bold text-green-600">
                        {systemInfo?.database_status || '正常'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Cog6ToothIcon className="h-8 w-8 text-gray-500" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">状态</h3>
                      <p className="text-2xl font-bold text-green-600">
                        运行中
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* System Statistics */}
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">系统统计</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {systemInfo?.total_quizzes || 0}
                    </p>
                    <p className="text-sm text-gray-600">题集总数</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {systemInfo?.total_users || 0}
                    </p>
                    <p className="text-sm text-gray-600">用户总数</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">
                      {systemInfo?.total_attempts || 0}
                    </p>
                    <p className="text-sm text-gray-600">答题总次数</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">
                      {systemInfo?.available_codes || 0}
                    </p>
                    <p className="text-sm text-gray-600">可用兑换码</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Database Tab */}
          {activeTab === 'database' && (
            <div className="space-y-6">
              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">数据库连接状态</h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CircleStackIcon className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                      <p className="text-lg font-medium text-gray-900">Cloudflare D1</p>
                      <p className="text-sm text-gray-600">数据库运行正常</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-400 rounded-full mr-2"></div>
                    <span className="text-sm text-green-600">在线</span>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">数据表状态</h3>
                <div className="space-y-3">
                  {[
                    { name: 'quizzes', description: '题集表' },
                    { name: 'users', description: '用户表' },
                    { name: 'attempts', description: '答题记录表' },
                    { name: 'codes_pool', description: '兑换码表' },
                    { name: 'award_claims', description: '获奖记录表' },
                    { name: 'events', description: '事件日志表' },
                  ].map(table => (
                    <div key={table.name} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                      <div>
                        <p className="font-mono text-sm text-gray-900">{table.name}</p>
                        <p className="text-xs text-gray-600">{table.description}</p>
                      </div>
                      <div className="flex items-center">
                        <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                        <span className="text-xs text-green-600">正常</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* SQL Console Tab */}
          {activeTab === 'sql' && (
            <div className="space-y-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex">
                  <ShieldCheckIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-2" />
                  <div>
                    <h3 className="text-sm font-medium text-yellow-800">注意</h3>
                    <p className="mt-1 text-sm text-yellow-700">
                      请谨慎使用SQL控制台，错误的操作可能会导致数据丢失。仅建议有经验的管理员使用。
                    </p>
                  </div>
                </div>
              </div>

              <div className="card">
                <h3 className="text-lg font-medium text-gray-900 mb-4">SQL 控制台</h3>
                <div className="space-y-4">
                  <div>
                    <label className="form-label">SQL 语句</label>
                    <textarea
                      value={sqlInput}
                      onChange={(e) => setSqlInput(e.target.value)}
                      className="form-textarea font-mono text-sm"
                      rows={8}
                      placeholder="输入SQL语句...

示例:
SELECT * FROM users LIMIT 10;
SELECT COUNT(*) FROM attempts WHERE completed_at IS NOT NULL;"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={handleExecuteSQL}
                      disabled={executeSQLMutation.isPending || !sqlInput.trim()}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <PlayIcon className="h-4 w-4" />
                      {executeSQLMutation.isPending ? '执行中...' : '执行'}
                    </button>
                    <button
                      onClick={() => setSqlInput('')}
                      className="btn btn-secondary"
                    >
                      清空
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}